import {
  OAuthAuthorizationRequestInfo,
  OAuthDecision,
} from "@markit/common.types";
import { API } from "../../API";
import { firebaseAuth } from "../../firebase";
import {
  getApiErrorMessage,
  getApiErrorStatus,
} from "../../utils/apiErrorUtils";

// Wraps the shared OAuth calls in result types the consent screen can switch
// on: an expired request and a switched account each get their own case
// instead of a generic error.

/**
 * A pending request lives for ten minutes, so "expired" is an ordinary outcome
 * rather than a failure, and gets its own case so the screen can explain it.
 */
export type FetchRequestResult =
  | { status: "ok"; request: OAuthAuthorizationRequestInfo }
  | { status: "expired" }
  | { status: "error"; message: string };

export type ConsentResult =
  | { status: "ok"; redirectUrl: string }
  | { status: "expired" }
  | { status: "accountMismatch" }
  | { status: "error"; message: string };

const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

/**
 * The backend already restricts callback URLs; this is a second check so the
 * browser is never sent to a javascript:/data: URL or a plain-http remote host.
 */
export const isSafeRedirectUrl = (redirectUrl: string): boolean => {
  let url: URL;
  try {
    url = new URL(redirectUrl);
  } catch {
    return false;
  }
  if (url.protocol === "https:") {
    return true;
  }
  return url.protocol === "http:" && LOOPBACK_HOSTS.has(url.hostname);
};

/** The backend answers 404 (or 410) once a request has expired or been used. */
const isExpired = (err: unknown) => {
  const status = getApiErrorStatus(err);
  return status === 404 || status === 410;
};

/** The endpoint is unauthenticated: it only returns display fields. */
export const fetchAuthorizationRequest = async (
  requestId: string
): Promise<FetchRequestResult> => {
  try {
    const request = await API.oauth.getAuthorizationRequest({ requestId });
    return { status: "ok", request };
  } catch (err) {
    return isExpired(err)
      ? { status: "expired" }
      : { status: "error", message: getApiErrorMessage(err) };
  }
};

const SESSION_MESSAGE =
  "We couldn't verify your Markit session. Try signing in again.";

/**
 * Records the user's decision and returns the client's callback URL to send the
 * browser to. Requires the Firebase ID token: this is the call that binds the
 * signed-in uid to the authorization code. expectedUid is the account the
 * screen showed; the token is only sent if it belongs to that same user, since
 * another tab can switch the Firebase user without this tab's Redux noticing.
 */
export const submitConsentDecision = async (
  requestId: string,
  decision: OAuthDecision,
  expectedUid: string
): Promise<ConsentResult> => {
  const user = firebaseAuth.currentUser;
  if (!user) {
    return { status: "error", message: SESSION_MESSAGE };
  }
  if (user.uid !== expectedUid) {
    return { status: "accountMismatch" };
  }
  let token: string;
  try {
    token = await user.getIdToken();
  } catch {
    return { status: "error", message: SESSION_MESSAGE };
  }

  try {
    const { redirectUrl } = await API.oauth.submitConsent(
      { requestId, decision },
      token
    );
    if (!redirectUrl) {
      return {
        status: "error",
        message: "Markit didn't return a place to send you back to. Try again from the app you're connecting to Markit.",
      };
    }
    return { status: "ok", redirectUrl };
  } catch (err) {
    return isExpired(err)
      ? { status: "expired" }
      : { status: "error", message: getApiErrorMessage(err) };
  }
};
